import { useEffect, useState } from "react";
import { http, API_BASE } from "@/lib/api";
import WeatherPulseNav from "./WeatherPulseNav";
import { Warning } from "@phosphor-icons/react";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import PageGuide from "@/components/PageGuide";

const abs = (u) => (!u ? "" : u.startsWith("http") ? u : `${API_BASE}${u}`);

const VERIFY_LABEL = {
  verified: "🟢 Verified",
  "high-confidence": "🔵 High confidence",
  unverified: "🟠 Unverified instrument",
  "needs-review": "🟠 Needs review",
  community: "🟡 Community observation",
  "ai-assisted": "⚪ AI-assisted",
};

/** "Weather Happening Now" — live global community observation feed. */
export default function WeatherNow() {
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = () => http.get("/weather-pulse/now").then(({ data }) => setData(data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  return (
    <main className="min-h-screen pt-32 md:pt-36 pb-24 bg-[color:var(--np-bg,#F5EBDC)] text-[#1B4332]" data-testid="wp-now-page">
      <div className="max-w-[1200px] mx-auto px-5 md:px-10">
        <ListenTranslateBar text="Weather happening now — live community reports from the last 24 hours." testidPrefix="ltbar-wp-now" />
        <div className="mt-4"><PageGuide pageKey="weather:now" testidPrefix="wp-now" /></div>
      </div>
      <div className="max-w-[1100px] mx-auto px-5 md:px-10">
        <WeatherPulseNav />
        <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2">/ Live</div>
        <h1 className="font-serif-display text-4xl md:text-5xl tracking-tight">Weather happening now.</h1>
        <p className="mt-2 text-sm text-[#3A3830] max-w-2xl">
          Community observations from the last 24 hours. Community reports are never official warnings — for severe weather, always follow your national meteorological service.
        </p>

        {!data ? (
          <div className="mt-10 font-mono-tech text-[10px] tracking-[0.3em] uppercase">Loading…</div>
        ) : (
          <>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="wp-now-stats">
              <div className="rounded-2xl bg-[#1B4332] text-[#F5EBDC] p-4">
                <div className="font-serif-display text-3xl">{data.total}</div>
                <div className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#E4A15C]">Observations</div>
              </div>
              <div className="rounded-2xl bg-[#0C4A6E] text-[#F5EBDC] p-4">
                <div className="font-serif-display text-3xl">{data.by_country.length}</div>
                <div className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#E4A15C]">Countries</div>
              </div>
              <div className="rounded-2xl bg-[#155E75] text-[#F5EBDC] p-4">
                <div className="font-serif-display text-3xl">{data.by_condition.length}</div>
                <div className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#E4A15C]">Conditions reported</div>
              </div>
              <div className="rounded-2xl bg-[#7C2D12] text-[#F5EBDC] p-4">
                <div className="font-serif-display text-3xl">{data.severe_reports}</div>
                <div className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#E4A15C]">Severe reports</div>
              </div>
            </div>

            {data.by_condition.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-2" data-testid="wp-now-conditions">
                {data.by_condition.map((c) => (
                  <span key={c.condition} className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-3 py-1.5 text-sm">
                    {c.emoji} {c.label} <b className="font-mono-tech text-[10px]">{c.count}</b>
                  </span>
                ))}
              </div>
            )}

            <h2 className="mt-8 font-serif-display text-2xl">Latest reports</h2>
            {data.latest.length === 0 ? (
              <p className="mt-3 text-sm text-[#3A3830]">No reports yet in this window.</p>
            ) : (
              <ul className="mt-3 grid sm:grid-cols-2 gap-2" data-testid="wp-now-latest">
                {data.latest.map((o, i) => (
                  <li key={o.id} data-testid={`wp-now-item-${i}`} className="rounded-xl border border-black/10 bg-white p-3 flex gap-3">
                    {o.photo && <img src={abs(o.photo)} alt="" loading="lazy" className="w-16 h-16 rounded-lg object-cover shrink-0 bg-black/5" />}
                    <div className="min-w-0">
                      <div className="font-medium text-sm">{o.emoji} {o.condition_label}</div>
                      <div className="text-xs text-[#3A3830] line-clamp-1">
                        {[o.city, o.state, o.country].filter(Boolean).join(", ") || ([o.lat, o.lon].filter((v) => v != null).join(", ") || "Location not shared")}
                        {o.measurements?.temperature_c != null && <> · {o.measurements.temperature_c}°C</>}
                        {o.measurements?.rainfall_mm != null && <> · {o.measurements.rainfall_mm} mm</>}
                      </div>
                      <div className="mt-1 font-mono-tech text-[8px] tracking-[0.18em] uppercase text-[color:var(--np-muted,#3A3830)]">
                        {VERIFY_LABEL[o.verification_status] || o.verification_status} · {(o.created_at || "").slice(5, 16).replace("T", " ")} UTC
                      </div>
                      {o.comment && <div className="mt-1 text-xs text-[#3A3830]/80 line-clamp-2">{o.comment}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-6 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex gap-2" data-testid="wp-now-warning-note">
              <Warning size={16} weight="fill" className="shrink-0 mt-0.5" />
              <div>These are community observations. Official warnings always come from your national weather service — Weather Pulse never replaces them.</div>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
